import {
  getCityFromPrediction,
  getCountyFromAddressComponents,
  getStateFromPrediction,
} from "@miplatform/utils";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

export const useMaps = () => {
  const geocoding = useMapsLibrary("geocoding");
  const places = useMapsLibrary("places");

  /**
   * Get latitude and longitude based on location parameters (state, city, address, zip).
   * @param {Object} params - The state name.
   * @returns {Promise<{lat: number, lng: number}|null>} - The coordinates or null if not found.
   */
  const getLatLng = async ({
    state,
    city = null,
    address = null,
    zip = null,
  }) => {
    if (!geocoding) {
      return null;
    }

    const geocoder = new geocoding.Geocoder();

    let coordinates = null;

    await geocoder.geocode(
      {
        address: `${address ? `${address}, ` : ""}${
          city ? `${city}, ` : ""
        }${state}${zip ? `, ${zip}` : ""}`,
      },
      (res, status) => {
        if (status === geocoding.GeocoderStatus.OK) {
          const { location } = res[0].geometry;
          coordinates = {
            lat: location.lat(),
            lng: location.lng(),
            isLocationPrecise: isLocationPrecise(res[0]),
          };
        }
      }
    );

    return coordinates;
  };

  const isLocationPrecise = (res) => {
    const locType = res.geometry.location_type;
    const isPartial = res.partial_match || false;

    return locType === "ROOFTOP" && !isPartial;
  };

  /**
   * Get zip code based on state and city.
   * @param {string} address - The street address line 1.
   * @param {string} city - The city name.
   * @param {string} state - The state name.
   * @returns {Promise<{lat: number, lng: number}|null>} - The coordinates or null if not found.
   */
  const getZip = async ({ address, city, state }) => {
    if (!geocoding) {
      return null;
    }

    const geocoder = new geocoding.Geocoder();

    let zip = "";

    await geocoder.geocode(
      { address: `${address}, ${city}, ${state}` },
      ([result], status) => {
        if (status === geocoding.GeocoderStatus.OK) {
          zip = result.address_components.find((component) =>
            component.types.includes("postal_code")
          ).long_name;
        }
      }
    );

    return zip;
  };

  /**
   * Search cities based on a query and optional state.
   * @param {string} query - The city search query.
   * @param {string|null} state - Optional state to restrict the search.
   * @returns {Promise<string[]>} - An array of city names.
   */
  const searchCities = async (query, state = null) => {
    if (!places) {
      return [];
    }

    const autoCompleteService = new places.AutocompleteService();
    const results = [];

    await autoCompleteService.getPlacePredictions(
      {
        input: `${query}${state ? `, ${state}` : ""}`,
        types: ["(cities)"],
        componentRestrictions: { country: "US" },
      },
      (predictions, status) => {
        if (status === places.PlacesServiceStatus.OK) {
          predictions.forEach(({ description }) => {
            const city = getCityFromPrediction(description, state);

            if (city) {
              results.push(city);
            }
          });
        }
      }
    );

    return results;
  };

  /**
   * Searches for residential addresses using the Google Places Autocomplete API.
   *
   * @async
   * @function searchAddresses
   * @param {string} query - The search query (e.g., "123 Main St").
   * @returns {Promise<string[]>} - A promise that resolves to an array of address descriptions.
   */
  const searchAddresses = async (query) => {
    if (!places) {
      return [];
    }

    const autoCompleteService = new places.AutocompleteService();
    const results = [];

    await autoCompleteService.getPlacePredictions(
      {
        input: query,
        types: ["address"],
        componentRestrictions: { country: "US" },
      },
      (predictions, status) => {
        if (status === places.PlacesServiceStatus.OK) {
          predictions.forEach(({ description }) => {
            results.push(description);
          });
        }
      }
    );

    return results;
  };

  /**
   * Retrieves the county for a given city and state.
   * @param {string} city - The name of the city to geocode.
   * @param {string} state - The name of the state to geocode.
   * @returns {Promise<string|null>} A promise that resolves to the county name or null if not found.
   */
  const getCounty = async (city, state) => {
    if (!geocoding) {
      return null;
    }

    const geocoder = new geocoding.Geocoder();
    let county = null;

    await geocoder.geocode({ address: `${city}, ${state}` }, (res, status) => {
      if (status === geocoding.GeocoderStatus.OK) {
        county = getCountyFromAddressComponents(res[0].address_components);
      }
    });

    return county;
  };

  const searchCityState = async (query) => {
    if (!places) {
      return [];
    }

    const autoCompleteService = new places.AutocompleteService();
    const results = [];

    await autoCompleteService.getPlacePredictions(
      {
        input: query,
        types: ["(cities)"],
        componentRestrictions: { country: "US" },
      },
      (predictions, status) => {
        if (status === places.PlacesServiceStatus.OK) {
          predictions.forEach(({ description }) => {
            const city = getCityFromPrediction(description);
            const state = getStateFromPrediction(description);

            if (city && state) {
              results.push({ city, state });
            }
          });
        }
      }
    );

    return results;
  };

  return {
    getLatLng,
    getZip,
    getCounty,
    searchCities,
    searchAddresses,
    searchCityState,
  };
};
